import type { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy, PageViewport, RenderTask } from 'pdfjs-dist';
import { FIT_GUTTER_PX, PageRenderer, RenderedPage, fetchFile } from './page-renderer';

/** Widest a fitted PDF page is drawn at 100% — a flyer stretched to a wide pane reads poorly. */
const MAX_FIT_WIDTH_PX: number = 820;

/**
 * PDF pages via pdf.js — a canvas per page plus a transparent text layer over it, so text can be
 * selected for a text pin.
 *
 * Zoom re-renders at the new scale rather than stretching the canvas, so text stays sharp.
 */
export class PdfRenderer implements PageRenderer {

    readonly paged: boolean = false;
    readonly hasText: boolean = true;

    private pdfjs: typeof import('pdfjs-dist') | null = null;
    /** Owns the worker; destroying it releases the document too. */
    private loadingTask: PDFDocumentLoadingTask | null = null;
    private doc: PDFDocumentProxy | null = null;
    private host: HTMLElement | null = null;
    private pages: HTMLElement[] = [];
    private tasks: RenderTask[] = [];
    /** Bumped on every zoom, so a slower earlier pass stops drawing once a newer one starts. */
    private pass: number = 0;

    async render(src: string, host: HTMLElement): Promise<RenderedPage[]> {
        this.host = host;
        this.pdfjs = await import('pdfjs-dist');
        // Copied in by the `angular.json` assets entry.
        this.pdfjs.GlobalWorkerOptions.workerSrc = 'assets/pdfjs/pdf.worker.min.mjs';

        const data: ArrayBuffer = await fetchFile(src);
        this.loadingTask = this.pdfjs.getDocument({ data: new Uint8Array(data) });
        this.doc = await this.loadingTask.promise;

        const rendered: RenderedPage[] = [];
        for (let number = 1; number <= this.doc.numPages; number++) {
            const el: HTMLDivElement = document.createElement('div');
            el.className = 'pv-page is-pdf';
            host.appendChild(el);
            this.pages.push(el);
            rendered.push({ el, label: `Page ${number}` });
        }

        await this.zoom(100);
        return rendered;
    }

    async zoom(zoom: number): Promise<void> {
        if (!this.doc || !this.host || !this.pdfjs) {
            return;
        }

        const pass: number = ++this.pass;
        this.cancelTasks();

        const firstPage: PDFPageProxy = await this.doc.getPage(1);
        const naturalWidth: number = firstPage.getViewport({ scale: 1 }).width;
        const fitWidth: number = Math.min(MAX_FIT_WIDTH_PX, this.host.clientWidth - FIT_GUTTER_PX);
        const scale: number = (Math.max(200, fitWidth) / naturalWidth) * (zoom / 100);

        for (let index = 0; index < this.pages.length; index++) {
            if (pass !== this.pass) {
                return;
            }
            const page: PDFPageProxy = await this.doc.getPage(index + 1);
            await this.drawPage(page, this.pages[index], page.getViewport({ scale }), pass);
        }
    }

    private async drawPage(page: PDFPageProxy, el: HTMLElement, viewport: PageViewport, pass: number): Promise<void> {
        if (!this.pdfjs) {
            return;
        }

        const ratio: number = window.devicePixelRatio || 1;
        const canvas: HTMLCanvasElement = document.createElement('canvas');
        canvas.className = 'pv-pdf-canvas';
        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const task: RenderTask = page.render({
            canvas,
            viewport,
            transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined
        });
        this.tasks.push(task);

        try {
            await task.promise;
        } catch {
            // Cancelled by a newer zoom pass — that pass draws the page instead.
            return;
        }
        if (pass !== this.pass) {
            return;
        }

        const textLayer: HTMLDivElement = document.createElement('div');
        textLayer.className = 'textLayer';
        await new this.pdfjs.TextLayer({
            textContentSource: await page.getTextContent(),
            container: textLayer,
            viewport
        }).render();
        if (pass !== this.pass) {
            return;
        }

        // Swap the new drawing in under the overlay, which the viewer keeps as the last child.
        el.style.width = `${Math.floor(viewport.width)}px`;
        el.style.height = `${Math.floor(viewport.height)}px`;
        el.style.setProperty('--scale-factor', String(viewport.scale));
        el.style.setProperty('--total-scale-factor', String(viewport.scale));
        el.querySelectorAll(':scope > .pv-pdf-canvas, :scope > .textLayer').forEach((old) => old.remove());
        el.prepend(canvas, textLayer);
    }

    private cancelTasks(): void {
        this.tasks.forEach((task) => task.cancel());
        this.tasks = [];
    }

    destroy(): void {
        this.pass++;
        this.cancelTasks();
        this.loadingTask?.destroy();
        this.loadingTask = null;
        this.doc = null;
        this.pages = [];
        this.host = null;
    }
}
